import { FormikContextType } from 'formik';
import { MessageDto, MessageStatus } from 'common/dto-types/message-dto';
import { NavigateFunction } from 'react-router';
import {
    MessageFormFields,
    publishMessageValidationSchema,
    sendTestMessageValidationSchema,
} from 'components/message-form/dto';
import {
    createMessage,
    updateMessage,
    sendTestMessage,
    publishMessage,
    deleteMessage,
    duplicateMessage,
    recallMessage,
} from 'pages/message/api';
import {
    convertMessageFormFieldValuesToDto,
    showSuccessToast,
    handleApiError,
    buildTouched,
} from 'components/message-form/utils';
import { ValidationError } from 'yup';
import set from 'lodash/set';

export type UseMessageActionsParams = {
    message?: MessageDto;
    formik: FormikContextType<MessageFormFields>;
    navigate: NavigateFunction;
};

export type UseMessageActionsReturn = {
    onCreate: () => Promise<void>;
    onUpdate: (exitAfter?: boolean) => Promise<void>;
    onDelete: () => Promise<void>;
    onDuplicate: () => Promise<void>;
    onPublish: () => Promise<void>;
    onRecall: () => Promise<void>;
    onSendTest: () => Promise<void>;
};

export const useMessageActions = ({ message, formik, navigate }: UseMessageActionsParams): UseMessageActionsReturn => {
    /**
     * Create a new message (status: Draft)
     */
    const onCreate = async () => {
        try {
            formik.setSubmitting(true);

            // Validate form
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                const touchedFields = buildTouched(errors);
                formik.setTouched(touchedFields);
                formik.setSubmitting(false);
                return;
            }

            const messageDto = convertMessageFormFieldValuesToDto(formik.values);
            await createMessage(messageDto);

            showSuccessToast('Message created successfully!');
            navigate('/message/draft');
        } catch (err: any) {
            handleApiError(err, 'create message', formik);
        }
    };

    /**
     * Update an existing message
     */
    const onUpdate = async (exitAfter: boolean = true) => {
        if (!message?.id) {
            handleApiError({ message: 'Cannot update: message ID is missing' }, 'update message', formik);
            return;
        }

        try {
            formik.setSubmitting(true);

            // Validate form
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                const touchedFields = buildTouched(errors);
                formik.setTouched(touchedFields);
                formik.setSubmitting(false);
                return;
            }

            const messageDto = convertMessageFormFieldValuesToDto(formik.values);
            await updateMessage(message.id, messageDto);

            showSuccessToast('Message updated successfully!');

            if (exitAfter) {
                navigate('/message/draft');
            } else {
                formik.setSubmitting(false);
            }
        } catch (err: any) {
            handleApiError(err, 'update message', formik);
        }
    };

    /**
     * Delete a draft message
     */
    const onDelete = async () => {
        if (!message?.id) {
            handleApiError({ message: 'Cannot delete: message ID is missing' }, 'delete message', formik);
            return;
        }

        try {
            formik.setSubmitting(true);
            await deleteMessage(message.id);
            showSuccessToast('Message deleted successfully!');
            navigate('/message/draft');
        } catch (err: any) {
            handleApiError(err, 'delete message', formik);
        }
    };

    /**
     * Duplicate an existing message
     */
    const onDuplicate = async () => {
        if (!message?.id) {
            handleApiError({ message: 'Cannot duplicate: message ID is missing' }, 'duplicate message', formik);
            return;
        }

        try {
            formik.setSubmitting(true);
            await duplicateMessage(message.id);
            showSuccessToast('Message duplicated successfully!');
            navigate(`/message/draft`);
        } catch (err: any) {
            handleApiError(err, 'duplicate message', formik);
        }
    };

    /**
     * Publish a message (change status to Published)
     */
    const onPublish = async () => {
        if (!message?.id) {
            handleApiError({ message: 'Cannot publish: message ID is missing' }, 'publish message', formik);
            return;
        }

        try {
            formik.setSubmitting(true);

            const errors = await publishMessageValidationSchema
                .validate(formik.values, { abortEarly: false })
                .then(() => ({}))
                .catch((err: unknown) => {
                    if (err instanceof ValidationError) {
                        const formErrors: Record<string, any> = {};
                        err.inner.forEach((e) => {
                            if (e.path) set(formErrors, e.path, e.message);
                        });
                        return formErrors;
                    }
                    throw err;
                });

            if (Object.keys(errors).length > 0) {
                const touchedFields = buildTouched(errors);

                // IMPORTANT: Set submitting to false BEFORE setting errors/touched
                // Otherwise Formik might clear the validation state
                formik.setSubmitting(false);

                // Set errors and touched after setSubmitting
                formik.setTouched(touchedFields, false);
                formik.setErrors(errors);

                return;
            }

            const messageDto = convertMessageFormFieldValuesToDto(formik.values);
            await publishMessage(message.id, messageDto);

            showSuccessToast('Message published successfully and will be sent to recipients shortly');
            navigate('/message/live');
        } catch (err: any) {
            handleApiError(err, 'publish message', formik);
        }
    };

    /**
     * Recall a published or scheduled message
     */
    const onRecall = async () => {
        if (!message?.id) {
            handleApiError({ message: 'Cannot recall: message ID is missing' }, 'recall message', formik);
            return;
        }
        try {
            formik.setSubmitting(true);
            await recallMessage(message.id);
            showSuccessToast('Message recalled successfully!');
            // Navigate based on new status
            if (message?.status === MessageStatus.Scheduled) {
                navigate('/message/draft');
            } else if (message?.status === MessageStatus.Published) {
                navigate('/message/archived');
            } else {
                navigate(-1);
            }
        } catch (err: any) {
            handleApiError(err, 'recall message', formik);
        } finally {
            formik.setSubmitting(false);
        }
    };

    /**
     * Send a test message to the current user's devices
     */
    const onSendTest = async () => {
        if (!message?.id) {
            handleApiError({ message: 'Cannot send test: message ID is missing' }, 'send test message', formik);
            return;
        }

        try {
            formik.setSubmitting(true);

            const errors = await sendTestMessageValidationSchema
                .validate(formik.values, { abortEarly: false })
                .then(() => ({}))
                .catch((err: unknown) => {
                    if (err instanceof ValidationError) {
                        const formErrors: Record<string, any> = {};
                        err.inner.forEach((e) => {
                            if (e.path) set(formErrors, e.path, e.message);
                        });
                        return formErrors;
                    }
                    throw err;
                });

            if (Object.keys(errors).length > 0) {
                const touchedFields = buildTouched(errors);

                // IMPORTANT: Set submitting to false BEFORE setting errors/touched
                // Otherwise Formik might clear the validation state
                formik.setSubmitting(false);

                // Set errors and touched after setSubmitting
                formik.setTouched(touchedFields, false);
                formik.setErrors(errors);

                return;
            }

            const messageDto = convertMessageFormFieldValuesToDto(formik.values);
            await sendTestMessage(message.id, messageDto);

            showSuccessToast('Message has been sent to your devices');
            formik.setSubmitting(false);
        } catch (err: any) {
            handleApiError(err, 'send test message', formik);
        }
    };

    return {
        onCreate,
        onUpdate,
        onDelete,
        onDuplicate,
        onPublish,
        onRecall,
        onSendTest,
    };
};
